/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Video,
  Headphones,
  Users,
  BookOpen,
  Heart,
  DollarSign,
  Sparkles,
  MessageCircle,
  Mic,
  ExternalLink,
  Brain,
  Dumbbell,
  Compass,
  Landmark,
  ArrowRight,
  Check,
  Flame,
  CalendarCheck,
  Quote,
} from 'lucide-react';
import PortalSubPage, { ComingSoonCard } from '../PortalSubPage';
import {
  NEXT_MEETING,
  INTEREST_MAILTO,
  INTEREST_EMAIL,
  CIRCLE_FOCUS,
  CIRCLE_GUEST_SPEAKER,
  CIRCLE_TOPICS,
  CIRCLE_PRICE,
  getReflectionForOffset,
} from '../../data/portalData';

const COACH_INQUIRY_EMAIL = 'nalvanzo@gmail.com';

const COACHES = [
  {
    name: 'Neil Alvanzo',
    role: 'Life Purpose Coach',
    desc: 'Certified life &amp; leadership coach. Purpose-Finder framework.',
    href: '/welcome/eric',
    contactEmail: COACH_INQUIRY_EMAIL,
    focus: 'Purpose, leadership, direction in the messy middle of a career or life pivot.',
  },
  {
    name: 'DeWayne Ellis',
    role: 'Wealth Strategist',
    desc: 'Financial planning, portfolios, and legacy strategy.',
    href: '/welcome/dewayne',
    contactEmail: COACH_INQUIRY_EMAIL,
    focus: 'Building wealth intentionally: investing, portfolio strategy, legacy planning.',
  },
  {
    name: 'David Girton',
    role: 'Wellness Coach',
    desc: 'The HEALth Debt Coach\u2122. Nervous-system regulation &amp; movement.',
    href: '/welcome/david-girton',
    contactEmail: COACH_INQUIRY_EMAIL,
    focus: 'Sleep, breath, nervous-system regulation, and moving your body back into alignment.',
  },
  {
    name: 'David Harper',
    role: 'Real Estate &amp; Wealth',
    desc: 'Real estate investing, funding, HUD, private capital.',
    href: '/welcome/david-harper',
    contactEmail: COACH_INQUIRY_EMAIL,
    focus: 'Real estate investing, private capital, HUD strategy, and building generational assets.',
  },
];

export function PortalCoaching() {
  return (
    <PortalSubPage
      eyebrow="Talk to a facilitator"
      title={<>Meet your <span className="italic text-[#D4AF37]">coaches.</span></>}
      intro="The men who lead in Panama are available year-round. Reach out for one-on-one coaching, ask what a session looks like, or just introduce yourself."
      icon={Mic}
    >
      <div className="grid md:grid-cols-2 gap-4">
        {COACHES.map((c) => {
          const mailto = `mailto:${c.contactEmail}?subject=${encodeURIComponent(
            `Coaching inquiry \u2014 ${c.name}`,
          )}&body=${encodeURIComponent(
            `Hey ${c.name.split(' ')[0]},\n\nI\u2019m interested in coaching around ${c.role.replace(
              /&amp;/g,
              '&',
            )}. Could you share what a session looks like, availability, and pricing?\n\nThanks,\n`,
          )}`;
          const nameSlug = c.name.split(' ')[0].toLowerCase();
          return (
            <div
              key={c.name}
              className="rounded-2xl bg-[#111111] border border-white/10 p-6 flex flex-col"
              data-testid={`coach-card-${nameSlug}`}
            >
              <p
                className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold mb-2"
                dangerouslySetInnerHTML={{ __html: c.role }}
              />
              <h3 className="font-cormorant text-2xl font-medium text-white mb-2">
                {c.name}
              </h3>
              <p
                className="text-zinc-400 font-manrope text-sm mb-3"
                dangerouslySetInnerHTML={{ __html: c.desc }}
              />
              {c.focus && (
                <p className="text-zinc-500 font-manrope text-xs leading-relaxed mb-5 italic border-l-2 border-[#D4AF37]/30 pl-3">
                  {c.focus}
                </p>
              )}

              <div className="mt-auto space-y-3">
                <a
                  href={mailto}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 rounded-full font-manrope text-sm font-semibold hover:bg-amber-200 transition-colors"
                  data-testid={`coach-contact-${nameSlug}`}
                >
                  <MessageCircle className="w-4 h-4" />
                  Email for coaching info
                </a>
                <a
                  href={`mailto:${c.contactEmail}`}
                  className="block text-zinc-500 hover:text-[#D4AF37] font-manrope text-xs truncate transition-colors"
                  data-testid={`coach-email-${nameSlug}`}
                >
                  {c.contactEmail}
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </PortalSubPage>
  );
}
